import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getUserId } from '../lib/supabase';

interface CrisisScenario {
  id: string;
  user_id: string;
  name: string;
  description: string;
  period_start: string;
  period_end: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ScenarioImpact {
  id: string;
  user_id: string;
  scenario_id: string;
  type: 'SHIFT' | 'SCALE' | 'ADD' | 'SPREAD';
  target_line_id?: string;
  amount?: number;
  percentage?: number;
  delay_days?: number;
  spread_periods?: number;
  label: string;
  created_at: string;
}

interface ScenarioResult {
  id: string;
  user_id: string;
  scenario_id: string;
  treasury_min: number;
  treasury_min_date: string;
  security_threshold: number;
  frd: number;
  rupture_date?: string;
  runway_days?: number;
  fixed_charges_monthly: number;
  calculated_at: string;
}

export function useCrisisScenarios() {
  const [scenarios, setScenarios] = useState<CrisisScenario[]>([]);
  const [activeScenario, setActiveScenario] = useState<CrisisScenario | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchScenarios = async () => {
    try {
      setLoading(true);
      const userId = await getUserId();

      const { data, error: fetchError } = await supabase
        .from('crisis_scenarios')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setScenarios(data || []);

      const active = data?.find(s => s.is_active) || null;
      setActiveScenario(active);

      setError(null);
    } catch (err) {
      console.error('Error fetching crisis scenarios:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch scenarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenarios();
  }, []);

  const createScenario = async (
    name: string,
    description: string,
    period_start: string,
    period_end: string
  ): Promise<string | null> => {
    try {
      const userId = await getUserId();

      const { data, error: insertError } = await supabase
        .from('crisis_scenarios')
        .insert({
          user_id: userId,
          name,
          description,
          period_start,
          period_end,
          is_active: false
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await fetchScenarios();
      return data.id;
    } catch (err) {
      console.error('Error creating scenario:', err);
      setError(err instanceof Error ? err.message : 'Failed to create scenario');
      return null;
    }
  };

  const updateScenario = async (
    scenarioId: string,
    updates: Partial<Omit<CrisisScenario, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('crisis_scenarios')
        .update(updates)
        .eq('id', scenarioId);

      if (updateError) throw updateError;

      await fetchScenarios();
      return true;
    } catch (err) {
      console.error('Error updating scenario:', err);
      setError(err instanceof Error ? err.message : 'Failed to update scenario');
      return false;
    }
  };

  const deleteScenario = async (scenarioId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('crisis_scenarios')
        .delete()
        .eq('id', scenarioId);

      if (deleteError) throw deleteError;

      await fetchScenarios();
      return true;
    } catch (err) {
      console.error('Error deleting scenario:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete scenario');
      return false;
    }
  };

  const setActive = async (scenarioId: string | null): Promise<boolean> => {
    try {
      const userId = await getUserId();

      await supabase
        .from('crisis_scenarios')
        .update({ is_active: false })
        .eq('user_id', userId);

      if (scenarioId) {
        const { error: updateError } = await supabase
          .from('crisis_scenarios')
          .update({ is_active: true })
          .eq('id', scenarioId);

        if (updateError) throw updateError;
      }

      await fetchScenarios();
      return true;
    } catch (err) {
      console.error('Error setting active scenario:', err);
      setError(err instanceof Error ? err.message : 'Failed to set active scenario');
      return false;
    }
  };

  return {
    scenarios,
    activeScenario,
    loading,
    error,
    fetchScenarios,
    createScenario,
    updateScenario,
    deleteScenario,
    setActive
  };
}

export function useScenarioImpacts(scenarioId: string | null) {
  const [impacts, setImpacts] = useState<ScenarioImpact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchImpacts = async () => {
    if (!scenarioId) {
      setImpacts([]);
      return;
    }

    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from('scenario_impacts')
        .select('*')
        .eq('scenario_id', scenarioId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setImpacts(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching scenario impacts:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch impacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImpacts();
  }, [scenarioId]);

  const addImpact = async (impact: Omit<ScenarioImpact, 'id' | 'user_id' | 'created_at'>): Promise<boolean> => {
    try {
      const userId = await getUserId();

      const { error: insertError } = await supabase
        .from('scenario_impacts')
        .insert({
          ...impact,
          user_id: userId
        });

      if (insertError) throw insertError;

      await fetchImpacts();
      return true;
    } catch (err) {
      console.error('Error adding impact:', err);
      setError(err instanceof Error ? err.message : 'Failed to add impact');
      return false;
    }
  };

  const deleteImpact = async (impactId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('scenario_impacts')
        .delete()
        .eq('id', impactId);

      if (deleteError) throw deleteError;

      await fetchImpacts();
      return true;
    } catch (err) {
      console.error('Error deleting impact:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete impact');
      return false;
    }
  };

  return {
    impacts,
    loading,
    error,
    fetchImpacts,
    addImpact,
    deleteImpact
  };
}

export function useScenarioResults(scenarioId: string | null) {
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResult = async () => {
    if (!scenarioId) {
      setResult(null);
      return;
    }

    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from('scenario_results')
        .select('*')
        .eq('scenario_id', scenarioId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      setResult(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching scenario result:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch result');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResult();
  }, [scenarioId]);

  const saveResult = async (
    scenarioId: string,
    resultData: Omit<ScenarioResult, 'id' | 'user_id' | 'scenario_id' | 'calculated_at'>
  ): Promise<boolean> => {
    try {
      const userId = await getUserId();

      const { error: upsertError } = await supabase
        .from('scenario_results')
        .upsert({
          user_id: userId,
          scenario_id: scenarioId,
          ...resultData,
          calculated_at: new Date().toISOString()
        }, {
          onConflict: 'scenario_id'
        });

      if (upsertError) throw upsertError;

      await fetchResult();
      return true;
    } catch (err) {
      console.error('Error saving scenario result:', err);
      setError(err instanceof Error ? err.message : 'Failed to save result');
      return false;
    }
  };

  return {
    result,
    loading,
    error,
    fetchResult,
    saveResult
  };
}

export type { CrisisScenario, ScenarioImpact, ScenarioResult };
